import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../components/AuthContext";

const EXAMPLE_QUERIES = [
  "Show me all loads from this week",
  "Find our top 5 customers by revenue",
  "What loads need carrier assignment?",
  "Show me delivered loads to Dallas",
  "What's our revenue this month?",
  "Find carriers in Texas",
  "What are my action items today?"
];

export default function AICopilot() {
  const { adminEmail } = useAuth();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "👋 Hi! I'm your AI Copilot. I can help you search loads, find carriers, check analytics, and even create or update shipments. What would you like to do?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCapabilities, setShowCapabilities] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const headers = { 
    'Content-Type': 'application/json',
    'x-admin-email': adminEmail, 
    'x-user-role': 'BROKER_ADMIN' 
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(text) {
    if (!text.trim()) return;
    
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/copilot/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: text, sessionId })
      });
      
      const data = await res.json();
      
      if (data.error) throw new Error(data.error);
      
      setSessionId(data.sessionId);
      
      // Add assistant response
      const assistantMessage = { 
        role: 'assistant', 
        content: data.response,
        toolsUsed: data.toolsUsed
      };
      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (e) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Error: ${e.message}. Please try again.`,
        isError: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  function clearConversation() {
    if (sessionId) {
      fetch(`/api/copilot/conversation/${sessionId}`, { method: 'DELETE', headers });
    }
    setMessages([
      { role: 'assistant', content: "👋 Conversation cleared! How can I help you?" }
    ]);
    setSessionId(null);
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 28 }}>🤖</span> AI Copilot
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 14 }}>
            Your intelligent assistant for freight operations
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            onClick={() => setShowCapabilities(!showCapabilities)}
            style={{ padding: '8px 16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            {showCapabilities ? '✕ Close' : '💡 What can I do?'}
          </button>
          <button 
            onClick={clearConversation}
            style={{ padding: '8px 16px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            🗑️ Clear
          </button>
        </div>
      </div>

      {/* Capabilities Panel */}
      {showCapabilities && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>What I can help you with:</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div>
              <strong>🔍 Search</strong>
              <ul style={{ margin: '4px 0', paddingLeft: 20, fontSize: 13, color: '#1e40af' }}>
                <li>Find loads by status, location, customer</li>
                <li>Search carriers and customers</li>
                <li>Look up specific shipments</li>
              </ul>
            </div>
            <div>
              <strong>📊 Analytics</strong>
              <ul style={{ margin: '4px 0', paddingLeft: 20, fontSize: 13, color: '#1e40af' }}>
                <li>Revenue and profit summaries</li>
                <li>Top customers and carriers</li>
                <li>Performance trends</li>
              </ul>
            </div>
            <div>
              <strong>⚡ Actions</strong>
              <ul style={{ margin: '4px 0', paddingLeft: 20, fontSize: 13, color: '#1e40af' }}>
                <li>Create new loads</li>
                <li>Update load status</li>
                <li>Assign carriers</li>
              </ul>
            </div>
            <div>
              <strong>💡 Recommendations</strong>
              <ul style={{ margin: '4px 0', paddingLeft: 20, fontSize: 13, color: '#1e40af' }}>
                <li>Best carrier for a load</li>
                <li>Daily action items</li>
                <li>Rate suggestions</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        {messages.map((msg, i) => (
          <div 
            key={i} 
            style={{ 
              display: 'flex', 
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 16
            }}
          >
            <div style={{ 
              maxWidth: '80%',
              padding: 12,
              borderRadius: 12,
              background: msg.role === 'user' ? '#dc2626' : msg.isError ? '#fee2e2' : '#f3f4f6',
              color: msg.role === 'user' ? 'white' : '#111827'
            }}>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{msg.content}</div>
              
              {/* Show tools used */}
              {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: 11, color: '#6b7280' }}>
                  🔧 Used: {msg.toolsUsed.map(t => t.tool).join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
            <div style={{ padding: 12, borderRadius: 12, background: '#f3f4f6' }}>
              <span style={{ animation: 'pulse 1s infinite' }}>🤔 Thinking...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Example Queries */}
      {messages.length <= 2 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Try asking:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {EXAMPLE_QUERIES.slice(0, 4).map((query, i) => (
              <button
                key={i}
                onClick={() => sendMessage(query)}
                style={{ 
                  padding: '6px 12px', 
                  background: '#f3f4f6', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 20, 
                  fontSize: 12, 
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => e.target.style.background = '#e5e7eb'}
                onMouseOut={e => e.target.style.background = '#f3f4f6'}
              >
                {query}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div style={{ display: 'flex', gap: 12 }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything about your loads, carriers, or analytics..."
          disabled={loading}
          style={{ 
            flex: 1, 
            padding: 16, 
            borderRadius: 12, 
            border: '2px solid #e5e7eb',
            fontSize: 15,
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
          onFocus={e => e.target.style.borderColor = '#dc2626'}
          onBlur={e => e.target.style.borderColor = '#e5e7eb'}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={loading || !input.trim()}
          style={{ 
            padding: '16px 24px', 
            background: loading || !input.trim() ? '#9ca3af' : '#dc2626', 
            color: 'white', 
            border: 'none', 
            borderRadius: 12, 
            fontWeight: 700, 
            cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
            fontSize: 15
          }}
        >
          {loading ? '...' : 'Send →'}
        </button>
      </div>
    </div>
  );
}
